import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createSuggestion,
  deleteSuggestion,
  getSuggestion,
  getSuggestions,
  updateSuggestion,
} from 'utils/funcs/knowledge-bases/suggestions'

// Query keys for React Query
export const SUGGESTIONS_KEYS = {
  all: ['suggestions'],
  lists: () => [...SUGGESTIONS_KEYS.all, 'list'],
  list: (knowledgeBaseId, filters) => [...SUGGESTIONS_KEYS.lists(), knowledgeBaseId, { filters }],
  details: () => [...SUGGESTIONS_KEYS.all, 'detail'],
  detail: id => [...SUGGESTIONS_KEYS.details(), id],
}

/**
 * Hook to fetch suggestions for a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {object} params - Query parameters
 * @param {string} params.type - Filter by type: 'SUGGESTION', 'FEEDBACK', or 'ALL'
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {object} options - React Query options
 */
export function useSuggestions(knowledgeBaseId, params = {}, options = {}) {
  return useQuery({
    queryKey: SUGGESTIONS_KEYS.list(knowledgeBaseId, params),
    queryFn: () => getSuggestions(knowledgeBaseId, params),
    enabled: !!knowledgeBaseId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

/**
 * Hook to fetch a specific suggestion
 * @param {string} suggestionId - Suggestion ID
 * @param {object} options - React Query options
 */
export function useSuggestion(suggestionId, options = {}) {
  return useQuery({
    queryKey: SUGGESTIONS_KEYS.detail(suggestionId),
    queryFn: () => getSuggestion(suggestionId),
    enabled: !!suggestionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

/**
 * Hook to create a new suggestion
 * @param {object} options - Mutation options
 */
export function useCreateSuggestion(options = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ knowledgeBaseId, data }) => createSuggestion(knowledgeBaseId, data),
    onSuccess: (data, variables) => {
      // Invalidate and refetch suggestions list for this KB
      queryClient.invalidateQueries({
        queryKey: SUGGESTIONS_KEYS.lists(),
        predicate: (query) => {
          // Check if this is a suggestions list query for the specific KB
          return query.queryKey[0] === 'suggestions'
            && query.queryKey[1] === 'list'
            && query.queryKey[2] === variables.knowledgeBaseId
        },
      })

      // Call onSuccess if provided
      options.onSuccess?.(data, variables)
    },
    onError: (error) => {
      console.error('Error creating suggestion:', error)
      options.onError?.(error)
    },
    ...options,
  })
}

/**
 * Hook to update a suggestion
 * @param {object} options - Mutation options
 */
export function useUpdateSuggestion(options = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ suggestionId, data }) => updateSuggestion(suggestionId, data),
    onSuccess: (data, variables) => {
      // Update the specific suggestion in cache
      queryClient.setQueryData(
        SUGGESTIONS_KEYS.detail(variables.suggestionId),
        old => ({ ...old, data }),
      )

      // Invalidate all suggestions lists to ensure consistency
      queryClient.invalidateQueries({
        predicate: (query) => {
          return query.queryKey[0] === 'suggestions' && query.queryKey[1] === 'list'
        },
      })

      options.onSuccess?.(data, variables)
    },
    onError: (error) => {
      console.error('Error updating suggestion:', error)
      options.onError?.(error)
    },
    ...options,
  })
}

/**
 * Hook to delete a suggestion
 * @param {object} options - Mutation options
 */
export function useDeleteSuggestion(options = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteSuggestion,
    onSuccess: (data, suggestionId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: SUGGESTIONS_KEYS.detail(suggestionId) })

      // Invalidate all suggestions lists to ensure consistency
      queryClient.invalidateQueries({
        predicate: (query) => {
          return query.queryKey[0] === 'suggestions' && query.queryKey[1] === 'list'
        },
      })

      options.onSuccess?.(data, suggestionId)
    },
    onError: (error) => {
      console.error('Error deleting suggestion:', error)
      options.onError?.(error)
    },
    ...options,
  })
}

/**
 * Utility function to invalidate all suggestions queries
 * @param {object} queryClient - React Query client
 */
export function invalidateSuggestionsQueries(queryClient) {
  queryClient.invalidateQueries({ queryKey: SUGGESTIONS_KEYS.all })
}

/**
 * Utility function to prefetch suggestions for a knowledge base
 * @param {object} queryClient - React Query client
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {object} params - Query parameters
 */
export function prefetchSuggestions(queryClient, knowledgeBaseId, params = {}) {
  return queryClient.prefetchQuery({
    queryKey: SUGGESTIONS_KEYS.list(knowledgeBaseId, params),
    queryFn: () => getSuggestions(knowledgeBaseId, params),
    staleTime: 5 * 60 * 1000,
  })
}
